import { useState, useEffect } from 'react';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { FileText, Code, Layers, Split, GitBranch, AlertTriangle, Upload } from 'lucide-react';
import { Template1ResumeData } from '@/components/ResumeTemplates/Template1';
import { PaginatedTemplate1 } from '@/components/ResumeTemplates/PaginatedTemplate1';
import { PageMargins } from '@/utils/resumePagination';
import { useToast } from '@/hooks/use-toast';

// Sample data for testing
const sampleData: Template1ResumeData = {
  Name: "Erica Merrick",
  Phone: "225-532-8794",
  Email: "emerrick2013@gmail.com",
  City: "Luling",
  State: "LA",
  LinkedIn: "www.linkedin.com/in/ericamerrick/",
  Summary: "Education Consultant with more than 20 years of experience in federal grant management and program compliance in K-12 education. Managed $40M+ in federal funds over tenure while securing $678K Freshman Academy grant and writing multiple successful grant applications totaling over $800K.",
  Skills: [
    { Skill: "Grant Writing" },
    { Skill: "Audit Preparation" },
    { Skill: "Policy Implementation" },
    { Skill: "Federal Grant Management" },
    { Skill: "Program Compliance" }
  ],
  CareerHighlights: [
    {
      chtitle: "Federal Grant Evaluation",
      chdescription: "Evaluated 16 federal grant proposals on 2 scoring panels spending 15 hours per week per review cycle while assessing 6 components with multiple criteria demonstrating federal evaluation capabilities."
    },
    {
      chtitle: "School Performance Growth",
      chdescription: "Raised school performance from D grade to A grade in 4 years while increasing dual enrollment 17% & AP pass rate by 0.7% demonstrating measurable academic improvement and leadership results."
    }
  ],
  ProfessionalExperience: [
    {
      job_title: "Peer Grant Reviewer",
      company: "U.S. Department of Education",
      location: "",
      start_date: "August 2024",
      end_date: "Present",
      Descriptions: [
        { Description: "Reviewed 16 federal grant proposals and thoroughly evaluated 6 components with 6 criteria per application cycle." },
        { Description: "Scored proposals based on overall quality and impact and ranked them within 2 collaborative review panels." },
        { Description: "Collaborated with federal program officers to ensure evaluation standards met Department requirements." }
      ]
    },
    {
      job_title: "Director of Federal Programs & School Improvement",
      company: "Algiers Charters",
      location: "New Orleans, LA",
      start_date: "July 2021",
      end_date: "October 2022",
      Descriptions: [
        { Description: "Researched and processed Title I–IV grants totaling $3M and secured $500K from 9th Grade Initiative Grant." },
        { Description: "Implemented $250K COVID tutoring and $56K REAL literacy programs serving underserved student populations." },
        { Description: "Supervised compliance monitoring for 12 federal programs across 3 campuses ensuring audit readiness." },
        { Description: "Trained 25 staff members on federal grant requirements and best practices for program implementation." }
      ]
    },
    {
      job_title: "Principal",
      company: "Hahnville High School",
      location: "Boutte, LA",
      start_date: "July 2016",
      end_date: "June 2021",
      Descriptions: [
        { Description: "Raised school letter grade from D to A in 4 years while increasing dual enrollment by 17%." },
        { Description: "Managed $2.5M annual budget and oversaw facility operations for 1,100 students and 85 staff members." },
        { Description: "Secured $678K Freshman Academy grant and wrote multiple successful grant applications totaling over $800K." }
      ]
    }
  ],
  Education: [
    {
      College_Degree_Name: "Master of Education",
      Major: "Administration & Leadership",
      College_Name: "Nicholls State University",
      City: "",
      ST: "",
      Year_Earned: "2004"
    },
    {
      College_Degree_Name: "Bachelor of Arts",
      Major: "Criminal Justice",
      College_Name: "Dillard University",
      City: "",
      ST: "",
      Year_Earned: "1995"
    }
  ],
  CertificationsAndLicenses: [
    {
      Certification_or_License_Name: "Educational Leader Level 3",
      Institution_Organization: "Louisiana",
      Year: "2020"
    }
  ],
  AwardsAndHonors: [
    {
      Award_Name: "Regional Principal of the Year",
      Institution_Organization: "St. John the Baptist Parish",
      Year: ""
    }
  ]
};

export default function ResumeTemplates() {
  const [resumeData, setResumeData] = useState<Template1ResumeData | null>(null);
  const [jsonInput, setJsonInput] = useState('');
  const [margins, setMargins] = useState<PageMargins>({ top: 1, bottom: 1, left: 1, right: 1 });
  const [showKeepTogether, setShowKeepTogether] = useState(false);
  const [showContinuation, setShowContinuation] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const { toast } = useToast();

  // Calculate maxHeight based on margins
  const maxHeight = (11 - margins.top - margins.bottom) * 96; // 96 DPI

  const handleLoadSample = () => {
    setResumeData(sampleData);
    toast({
      title: "Sample data loaded",
      description: "Resume preview updated with sample data"
    });
  };

  const handleLoadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setResumeData(parsed);
      toast({
        title: "Success",
        description: "JSON data loaded successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Invalid JSON format. Please check your data.",
        variant: "destructive"
      });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      setResumeData(parsed);
      setJsonInput(text);
      toast({
        title: "Success",
        description: "File loaded successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to parse JSON file.",
        variant: "destructive"
      });
    }
  };

  // Inject CSS for debug overlays
  useEffect(() => {
    if (!showKeepTogether && !showContinuation) return;

    const style = document.createElement('style');
    style.id = 'pagination-debug-styles';
    style.textContent = `
      ${showKeepTogether ? `
        [data-keep-together="true"] {
          outline: 2px solid rgba(59, 130, 246, 0.5) !important;
          outline-offset: 2px;
          background-color: rgba(59, 130, 246, 0.05) !important;
        }
      ` : ''}
      ${showContinuation ? `
        [data-experience-continuation="true"] {
          outline: 2px solid rgba(34, 197, 94, 0.5) !important;
          outline-offset: 2px;
          background-color: rgba(34, 197, 94, 0.05) !important;
        }
      ` : ''}
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById('pagination-debug-styles');
      if (existingStyle) existingStyle.remove();
    };
  }, [showKeepTogether, showContinuation]);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Resume Templates – Pagination Architecture"
        description="Developer-facing demo of the 6-phase pagination system"
        backTo="/"
      />

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[480px_1fr] gap-6">
          {/* Left Column: Documentation & Controls */}
          <div className="space-y-6">
            {/* Phase 1: Height Budget */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-primary" />
                  Phase 1: Height Budget Calculation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-muted p-4 font-mono text-sm">
                  <div className="text-muted-foreground mb-2">Formula:</div>
                  <code className="text-primary">maxHeight = (11in - topMargin - bottomMargin) × 96 DPI</code>
                </div>
                <div className="rounded-lg border-2 border-primary/20 bg-primary/5 p-4">
                  <div className="text-sm font-semibold text-primary mb-1">Current Budget:</div>
                  <div className="text-2xl font-bold text-foreground">{maxHeight}px</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Available height per page with current margins
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Adjust Margins (inches)</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="margin-top">Top</Label>
                      <Input
                        id="margin-top"
                        type="number"
                        step="0.25"
                        min="0.25"
                        max="2"
                        value={margins.top}
                        onChange={(e) => setMargins({ ...margins, top: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="margin-bottom">Bottom</Label>
                      <Input
                        id="margin-bottom"
                        type="number"
                        step="0.25"
                        min="0.25"
                        max="2"
                        value={margins.bottom}
                        onChange={(e) => setMargins({ ...margins, bottom: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="margin-left">Left</Label>
                      <Input
                        id="margin-left"
                        type="number"
                        step="0.25"
                        min="0.25"
                        max="2"
                        value={margins.left}
                        onChange={(e) => setMargins({ ...margins, left: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="margin-right">Right</Label>
                      <Input
                        id="margin-right"
                        type="number"
                        step="0.25"
                        min="0.25"
                        max="2"
                        value={margins.right}
                        onChange={(e) => setMargins({ ...margins, right: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phase 2: Shadow DOM Measurement */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="w-5 h-5 text-primary" />
                  Phase 2: Shadow DOM Measurement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>Each section is cloned into a hidden, fixed-width container to measure its true height.</p>
                  <div className="rounded-lg bg-muted p-3 font-mono text-xs">
                    <code>
                      temp.style.position = 'absolute';<br />
                      temp.style.visibility = 'hidden';<br />
                      temp.style.width = '{(8.5 - margins.left - margins.right) * 96}px';<br />
                      const height = temp.scrollHeight;
                    </code>
                  </div>
                  <p className="text-xs">This ensures measurements match the final rendered layout exactly.</p>
                </div>
              </CardContent>
            </Card>

            {/* Phase 3: State Machine */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-primary" />
                  Phase 3: State Machine Decision Tree
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="rounded-lg border-l-4 border-primary/50 bg-muted/50 p-3">
                    <div className="font-semibold mb-1">1. Does it fit?</div>
                    <div className="text-muted-foreground text-xs">If height ≤ availableHeight → Place on current page</div>
                  </div>
                  <div className="rounded-lg border-l-4 border-blue-500/50 bg-blue-50/50 dark:bg-blue-950/20 p-3">
                    <div className="font-semibold mb-1">2. Is it atomic?</div>
                    <div className="text-muted-foreground text-xs">
                      If data-keep-together OR data-experience-continuation:<br />
                      → Start new page<br />
                      → If still too big → Split recursively
                    </div>
                  </div>
                  <div className="rounded-lg border-l-4 border-green-500/50 bg-green-50/50 dark:bg-green-950/20 p-3">
                    <div className="font-semibold mb-1">3. Can split?</div>
                    <div className="text-muted-foreground text-xs">
                      Split at semantic boundaries (LI elements)<br />
                      → Top part fills current page<br />
                      → Bottom part recurses to new page
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Switch
                    id="debug-mode"
                    checked={debugMode}
                    onCheckedChange={setDebugMode}
                  />
                  <Label htmlFor="debug-mode" className="text-sm">Show decision logs (console)</Label>
                </div>
              </CardContent>
            </Card>

            {/* Phase 4: Content Splitting */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Split className="w-5 h-5 text-primary" />
                  Phase 4: Intelligent Content Splitting
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>When content doesn't fit, the system splits at semantic boundaries:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs ml-2">
                    <li>Lists (UL/OL) split at LI boundaries</li>
                    <li>Headings marked with data-stick-with-next-list stay with their content</li>
                    <li>Prevents orphaned headers at page bottoms</li>
                    <li>Prevents widow list items at page tops</li>
                  </ul>
                  <div className="rounded-lg bg-muted p-3 font-mono text-xs">
                    <code>
                      // Split UL at LI boundary<br />
                      listTop.appendChild(li);<br />
                      // Until exceeds remaining height<br />
                      listBottom.appendChild(remaining LIs);
                    </code>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phase 5: Experience Semantics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Phase 5: Experience Section Semantics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p className="font-semibold text-foreground">Atomic Blocks (All-or-Nothing):</p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          keep-together
                        </Badge>
                        <span className="text-xs">Section header + first job stay together</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          experience-continuation
                        </Badge>
                        <span className="text-xs">Subsequent jobs are atomic (move to new page if don't fit)</span>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Visual Debug Overlays</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="show-keep-together" className="text-sm">Highlight keep-together</Label>
                        <Switch
                          id="show-keep-together"
                          checked={showKeepTogether}
                          onCheckedChange={setShowKeepTogether}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="show-continuation" className="text-sm">Highlight experience-continuation</Label>
                        <Switch
                          id="show-continuation"
                          checked={showContinuation}
                          onCheckedChange={setShowContinuation}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phase 6: Guards */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Phase 6: Overflow & Edge Case Guards
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 p-3">
                    <div className="font-semibold text-amber-900 dark:text-amber-200 mb-1">No-Progress Guard</div>
                    <div className="text-xs">Detects when split() produces empty top part → Forces placement to avoid infinite loops</div>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50/50 dark:bg-red-950/20 p-3">
                    <div className="font-semibold text-red-900 dark:text-red-200 mb-1">Iteration Cap (200)</div>
                    <div className="text-xs">Prevents runaway while-loops in pathological cases</div>
                  </div>
                  <div className="rounded-lg border border-purple-200 bg-purple-50/50 dark:bg-purple-950/20 p-3">
                    <div className="font-semibold text-purple-900 dark:text-purple-200 mb-1">Widow/Orphan Prevention</div>
                    <div className="text-xs">Elements with data-stick-with-next-list move to next page to avoid orphaned headings</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Input */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-primary" />
                  Load Resume Data
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={handleLoadSample} variant="outline" className="w-full">
                  Load Sample Data
                </Button>
                
                <Separator />
                
                <div className="space-y-2">
                  <Label htmlFor="json-file">Upload JSON File</Label>
                  <Input
                    id="json-file"
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                  />
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <Label htmlFor="json-paste">Or Paste JSON</Label>
                  <Textarea
                    id="json-paste"
                    placeholder='{"Name": "Your Name", ...}'
                    className="min-h-[120px] font-mono text-xs"
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                  />
                  <Button onClick={handleLoadJson} disabled={!jsonInput.trim()} className="w-full">
                    Load JSON
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Live Preview */}
          <div className="lg:sticky lg:top-6 lg:h-[calc(100vh-3rem)]">
            <Card className="h-full flex flex-col">
              <CardHeader>
                <CardTitle>Live Paginated Preview</CardTitle>
                <CardDescription>
                  {resumeData ? 'Scroll to see all pages' : 'Load data to see preview'}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 p-0 overflow-hidden">
                {resumeData ? (
                  <ScrollArea className="h-full w-full">
                    <div className="bg-gray-100 dark:bg-gray-900 p-8 min-h-full flex flex-col items-center gap-4">
                      <PaginatedTemplate1 data={resumeData} margins={margins} />
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
                      <p>Load resume data to see paginated preview</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
